import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import augentLogo from '../../assets/AugentColor.svg';
import { isAuthenticated, persistAppSession } from '../../auth-utils';
import { azureAdConfig } from '../../config/azureAd';
import { createLocalSession, loginWithMicrosoft } from '../../services/authService';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f5f5f5;
`;

const LoginCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.7rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 400px;
  margin: 20px auto;
`;

const LoginForm = styled.form`
  margin-top: 1.5rem;
`;

const InputField = styled.input`
  width: 95%;
  padding: 0.75rem;
  margin: 0.5rem 0 1rem;
  border: 1px solid #ddd;
  border-radius: 0.5rem;

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem;
  background: #6b21a8;
  color: white;
  border: 2px solid #6b21a8;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: bold;
  width: 100%;
  margin: 0 auto;

  &:hover:not(:disabled) {
    background-color: #553285;
    border-color: #553285;
  }

  &:disabled {
    background: #ccc;
    border-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: transparent;
  color: #6b21a8;
  margin-top: 0.75rem;

  &:hover:not(:disabled) {
    background-color: #6b21a8;
    color: white;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #9ca3af;
  font-size: 0.875rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
  }

  span {
    padding: 0 0.75rem;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  margin-top: 1rem;
  text-align: center;
`;

const RedirectNotice = styled.div`
  background: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #1565c0;
  font-weight: 500;
`;

const Heading = styled.h1`
  color: #6b21a8;
  font-size: 2rem;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subheading = styled.p`
  color: #6b7280;
  text-align: center;
  margin-bottom: 0;
`;

export const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const navigate = useNavigate();
  const showLocalAuth = azureAdConfig.enableLocalAuth;

  useEffect(() => {
    if (isAuthenticated()) {
      window.location.replace('/apps');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    if (redirect) {
      setRedirectPath(redirect);
    }
  }, [navigate]);

  const completeLogin = (
    sessionExpiry: number,
    user: { id: string; username: string; email?: string; name?: string; role: string },
    authProvider: string
  ) => {
    persistAppSession(user, sessionExpiry, authProvider);

    if (redirectPath) {
      const target = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
      window.location.replace(target);
    } else {
      window.location.replace('/apps');
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const redirect = redirectPath || urlParams.get('redirect');
      await loginWithMicrosoft(redirect);
    } catch (err) {
      console.error('Microsoft login error:', err);
      setError('Unable to start Microsoft sign-in. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showLocalAuth) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      const payload = await createLocalSession(username.trim(), password);
      setUsername('');
      setPassword('');
      completeLogin(payload.expiry, payload.user, payload.authProvider);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during login');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <img src={augentLogo} alt="AugentLogo" width="300px" />
      <LoginCard>
        <Heading>Agentic AI Apps</Heading>
        <Subheading>Sign in to access AI applications</Subheading>

        {redirectPath && (
          <RedirectNotice style={{ marginTop: '1rem' }}>
            Please sign in to access {redirectPath.replace(/^\//, '').toUpperCase()}
          </RedirectNotice>
        )}

        <PrimaryButton
          type="button"
          onClick={handleMicrosoftLogin}
          disabled={loading}
          style={{ marginTop: '1.5rem' }}
        >
          {loading ? 'Redirecting...' : 'Sign in with Microsoft'}
        </PrimaryButton>

        {showLocalAuth && (
          <>
            <Divider>
              <span>or use password</span>
            </Divider>

            <LoginForm onSubmit={handleSubmit}>
              <div>
                <label htmlFor="username">Username:</label>
                <InputField
                  type="text"
                  id="username"
                  value={username}
                  placeholder="Username or email"
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="username"
                />
              </div>
              <div>
                <label htmlFor="password">Password:</label>
                <InputField
                  type="password"
                  id="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="current-password"
                />
              </div>
              <SecondaryButton type="submit" disabled={loading}>
                {loading ? 'Logging in...' : 'Login with Password'}
              </SecondaryButton>
            </LoginForm>
          </>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </LoginCard>
    </LoginContainer>
  );
};
